import React, { useMemo, useState, useId, useCallback, useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import PropTypes from 'prop-types';
import { authFetch, API_BASE } from '../auth';
import './BuyOnClose.css';
import TickerAutocomplete from '../components/TickerAutocomplete';
import TickerIcon from '../components/TickerIcon';
import TickerLabel from '../components/TickerLabel';

const LEGS = ['core', 'lotto', 'hedge'];
const POLL_MS = 5000;

function parseTickers(raw) {
  return raw.split(',').map((t) => t.trim().toUpperCase()).filter(Boolean);
}

function createEmptyTicker() {
  const base = {
    execute_at_time: '',
    order_expiry_time: '',
  };
  LEGS.forEach((leg) => {
    base[`${leg}_strike_mode`] = 'pct';
    base[`${leg}_strike_pct`] = '';
    base[`${leg}_exact_strike`] = '';
    base[`${leg}_dollar_dev`] = '';
    base[`${leg}_premium_mode`] = 'pct';
    base[`${leg}_premium_pct`] = '';
    base[`max_amount_${leg}`] = '';
    base[`${leg}_direction`] = '';
    base[`${leg}_expiry_date`] = '';
  });
  return base;
}

function safeId(str) {
  return String(str).replace(/[^a-z0-9_-]/gi, '_');
}

function validateForm(formData) {
  const errs = new Set();
  Object.entries(formData).forEach(([ticker, d]) => {
    const key = (field) => `${ticker}.${field}`;
    if (!d.execute_at_time) errs.add(key('execute_at_time'));
    LEGS.forEach((leg) => {
      const mode = d[`${leg}_strike_mode`] || 'pct';
      if (mode === 'exact' && !d[`${leg}_exact_strike`]) errs.add(key(`${leg}_exact_strike`));
      if (mode === 'pct' && !d[`${leg}_strike_pct`]) errs.add(key(`${leg}_strike_pct`));
      if (mode === 'dollar' && !d[`${leg}_dollar_dev`]) errs.add(key(`${leg}_dollar_dev`));
      if (!d[`${leg}_premium_pct`]) errs.add(key(`${leg}_premium_pct`));
      if (!d[`max_amount_${leg}`]) errs.add(key(`max_amount_${leg}`));
      if (!d[`${leg}_direction`]) errs.add(key(`${leg}_direction`));
      if (!d[`${leg}_expiry_date`]) errs.add(key(`${leg}_expiry_date`));
    });
  });
  return errs;
}

const STATUS_LABELS = {
  active: 'Active',
  filled: 'Filled',
  expired: 'Expired',
  cancelled: 'Cancelled',
  error: 'Error',
};

/* ---------- ActiveOrders ---------- */

function ActiveOrders({ orders, onCancel, onClear }) {
  if (orders.length === 0) return null;
  const hasCompleted = orders.some((o) => o.status !== 'active');
  return (
    <section className="boc__orders">
      <div className="boc__orders-head">
        <h2 className="boc__orders-title">Active Orders</h2>
        {hasCompleted && (
          <button type="button" className="boc__ghost-btn" onClick={onClear}>
            Clear completed
          </button>
        )}
      </div>
      <div className="boc__order-list">
        {orders.map((o) => (
          <div className={`boc__order-row boc__order-row--${o.status}`} key={o.id}>
            <div className="boc__order-ticker">{o.ticker}</div>
            <div className={`boc__order-badge boc__order-badge--${o.status}`}>
              {STATUS_LABELS[o.status] || o.status}
            </div>
            <div className="boc__order-msg">{o.message}</div>
            <div className="boc__order-time">{o.created_at}</div>
            {o.status === 'active' && (
              <button
                type="button"
                className="boc__ghost-btn boc__ghost-btn--sm"
                onClick={() => onCancel(o.id)}
              >
                Cancel
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

ActiveOrders.propTypes = {
  orders: PropTypes.array.isRequired,
  onCancel: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

/* ---------- OrderSummaryModal ---------- */

function OrderSummaryModal({ formData, onConfirm, onCancel }) {
  const now = new Date().toLocaleString('en-US', {
    dateStyle: 'full',
    timeStyle: 'short',
  });
  const tickerList = Object.entries(formData);

  return (
    <div
      className="pbModal__backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="boc-modal-title"
    >
      <div className="pbModal">
        <div className="pbModal__header">
          <div>
            <h2 className="pbModal__title" id="boc-modal-title">
              Confirm Order Submission
            </h2>
            <p className="pbModal__subtitle">
              Review all orders carefully before confirming.
            </p>
          </div>
        </div>

        <div className="pbModal__body" id="pb-print-area">
          <div className="pbPrintHeader">
            <div className="pbPrintBrand">Buy on Close</div>
            <h1 className="pbPrintTitle">Order Summary</h1>
            <div className="pbPrintMeta">
              <span>Generated: {now}</span>
              <span>
                {tickerList.length} ticker{tickerList.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          {tickerList.map(([ticker, data], i) => (
            <div className="pbSummaryTicker" key={ticker}>
              <div className="pbSummaryTicker__header">
                <span className="pbSummaryTicker__num">{i + 1}</span>
                <span className="pbSummaryTicker__name"><TickerIcon symbol={ticker} size={16} /> {ticker}</span>
              </div>

              <div className="pbSummaryBody">
                <section className="pbSummarySection">
                  <div className="pbSummarySection__title">Time Trigger</div>
                  <dl className="pbSummaryDl">
                    <dt>Execute at</dt>
                    <dd>{data.execute_at_time} CST</dd>
                    {data.order_expiry_time && (
                      <>
                        <dt>Expires at</dt>
                        <dd>{data.order_expiry_time} CST</dd>
                      </>
                    )}
                  </dl>
                </section>

                <section className="pbSummarySection">
                  <div className="pbSummarySection__title">Legs</div>
                  <div className="pbSummaryLegs">
                    {LEGS.map((leg) => (
                      <div className="pbSummaryLeg" key={leg}>
                        <div className="pbSummaryLeg__name">{leg.toUpperCase()}</div>
                        <dl className="pbSummaryDl">
                          <dt>Direction</dt>
                          <dd>{data[`${leg}_direction`] === 'C' ? 'Call' : 'Put'}</dd>
                          <dt>Strike</dt>
                          <dd>
                            {data[`${leg}_strike_mode`] === 'exact'
                              ? `$${data[`${leg}_exact_strike`]} (exact)`
                              : data[`${leg}_strike_mode`] === 'dollar'
                              ? `$${data[`${leg}_dollar_dev`]} deviation`
                              : `${data[`${leg}_strike_pct`]}% from price`}
                          </dd>
                          <dt>Premium</dt>
                          <dd>
                            {data[`${leg}_premium_mode`] === 'dollar'
                              ? `$${data[`${leg}_premium_pct`]} cap`
                              : `${data[`${leg}_premium_pct`]}%`}
                          </dd>
                          <dt>Max amount</dt>
                          <dd>${data[`max_amount_${leg}`]}</dd>
                          <dt>Expiry date</dt>
                          <dd>{data[`${leg}_expiry_date`]}</dd>
                        </dl>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            </div>
          ))}
        </div>

        <div className="pbModal__footer">
          <button type="button" className="pbGhostBtn" onClick={onCancel}>
            Go back
          </button>
          <button
            type="button"
            className="pbGhostBtn"
            onClick={() => window.print()}
          >
            Print
          </button>
          <button type="button" className="pbPrimaryBtn" onClick={onConfirm}>
            Confirm &amp; Submit
          </button>
        </div>
      </div>
    </div>
  );
}

OrderSummaryModal.propTypes = {
  formData: PropTypes.object.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

/* ---------- main component ---------- */

export default function BuyOnClose() {
  const uid = useId();
  const [tickerInput, setTickerInput] = useState('');
  const [formData, setFormData] = useState({});
  const [status, setStatus] = useState({ state: 'idle', message: '' });
  const [errors, setErrors] = useState(new Set());
  const [orders, setOrders] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const pollRef = useRef(null);

  const tickers = useMemo(() => parseTickers(tickerInput), [tickerInput]);
  const hasUnsavedData = tickers.length > 0;

  const hasError = useCallback(
    (ticker, field) => errors.has(`${ticker}.${field}`),
    [errors]
  );

  // ── in-app navigation blocker ──
  const blocker = useBlocker(hasUnsavedData);

  // ── warn on tab close / page reload ──
  useEffect(() => {
    const handler = (e) => {
      if (hasUnsavedData) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedData]);

  // ── polling ──
  const fetchStatus = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/buy-on-close/status/`);
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
      }
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchStatus]);

  const handleCancel = async (orderId) => {
    try {
      await authFetch(`${API_BASE}/buy-on-close/cancel/${orderId}/`, { method: 'POST' });
      fetchStatus();
    } catch {
      /* silent */
    }
  };

  const handleClear = async () => {
    try {
      await authFetch(`${API_BASE}/buy-on-close/clear/`, { method: 'POST' });
      fetchStatus();
    } catch {
      /* silent */
    }
  };

  // ── form handlers ──

  const handleTickerChange = (raw) => {
    setTickerInput(raw);
    const nextTickers = parseTickers(raw);
    setFormData((prev) => {
      const next = {};
      nextTickers.forEach((t) => {
        next[t] = { ...createEmptyTicker(), ...(prev[t] || {}) };
      });
      return next;
    });
  };

  const removeTicker = (ticker) => {
    const nextTickers = tickers.filter((t) => t !== ticker);
    setTickerInput(nextTickers.join(', '));
    setFormData((prev) => {
      const next = { ...prev };
      delete next[ticker];
      return next;
    });
    setErrors((prev) => {
      const next = new Set(prev);
      for (const k of prev) {
        if (k.startsWith(`${ticker}.`)) next.delete(k);
      }
      return next;
    });
  };

  const handleFieldChange = (ticker, field, value) => {
    setFormData((prev) => {
      const tickerData = { ...prev[ticker], [field]: value };
      if (field === 'core_direction') {
        if (value === 'C') {
          tickerData.lotto_direction = 'C';
          tickerData.hedge_direction = 'P';
        } else if (value === 'P') {
          tickerData.lotto_direction = 'P';
          tickerData.hedge_direction = 'C';
        } else {
          tickerData.lotto_direction = '';
          tickerData.hedge_direction = '';
        }
      }
      return { ...prev, [ticker]: tickerData };
    });
    const errKey = `${ticker}.${field}`;
    if (errors.has(errKey)) {
      setErrors((prev) => {
        const next = new Set(prev);
        next.delete(errKey);
        if (field === 'core_direction') {
          next.delete(`${ticker}.lotto_direction`);
          next.delete(`${ticker}.hedge_direction`);
        }
        return next;
      });
    } else if (field === 'core_direction' && value) {
      setErrors((prev) => {
        const next = new Set(prev);
        next.delete(`${ticker}.lotto_direction`);
        next.delete(`${ticker}.hedge_direction`);
        return next;
      });
    }
  };

  // validate → show confirm modal
  const handleSubmit = (e) => {
    e.preventDefault();

    const errs = validateForm(formData);
    setErrors(errs);
    if (errs.size > 0) {
      setStatus({
        state: 'error',
        message: `${errs.size} required field${errs.size > 1 ? 's' : ''} missing`,
      });
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmedSubmit = async () => {
    setShowConfirmModal(false);

    const payload = { data: {} };
    Object.entries(formData).forEach(([ticker, fields]) => {
      payload.data[ticker] = { ...fields };
    });

    setStatus({ state: 'loading', message: 'Submitting\u2026' });

    try {
      const res = await authFetch(`${API_BASE}/buy-on-close/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || data?.message || 'Request failed');

      const submitted = Object.keys(data.order_ids || {}).join(', ');
      setStatus({ state: 'success', message: `Submitted: ${submitted}` });
      fetchStatus();
    } catch (err) {
      setStatus({ state: 'error', message: err?.message || 'Error submitting strategy' });
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-header__title">Buy on Close</h2>
        <p className="page-header__subtitle">
          Schedule timed buy orders — legs execute when the clock hits your target time.
          Submit multiple orders; they run concurrently.
        </p>
      </div>

      <ActiveOrders orders={orders} onCancel={handleCancel} onClear={handleClear} />

      <form onSubmit={handleSubmit} className="boc__form">
        <section className="boc__card">
          <div className="boc__card-head">
            <h2 className="boc__card-title">Tickers</h2>
            <div className="boc__meta">{tickers.length} ticker(s)</div>
          </div>

          <div className="boc__grid boc__grid--2">
            <div className="boc__field">
              <label className="label" htmlFor={`boc-${uid}-tickers`}>
                Tickers (comma-separated)
              </label>
              <div className="boc__field-hint">Example: AAPL, MSFT, SPY</div>
              <TickerAutocomplete
                id={`boc-${uid}-tickers`}
                className="input"
                value={tickerInput}
                onChange={handleTickerChange}
                placeholder="AAPL, MSFT"
              />
            </div>

            <div className="boc__chips" aria-label="Current tickers">
              {tickers.length === 0 ? (
                <div className="boc__hint-box">Add at least one ticker to begin.</div>
              ) : (
                tickers.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className="boc__chip"
                    onClick={() => removeTicker(t)}
                    title={`Remove ${t}`}
                  >
                    <TickerIcon symbol={t} size={16} />
                    <span>{t}</span>
                    <span aria-hidden="true">&times;</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </section>

        {Object.keys(formData).map((ticker) => {
          const tSafe = safeId(ticker);
          const d = formData[ticker];

          return (
            <section className={`boc__ticker-card${d.core_direction === 'C' ? ' boc__ticker-card--call' : d.core_direction === 'P' ? ' boc__ticker-card--put' : ''}`} key={ticker}>
              <div className="boc__ticker-card-head">
                <div>
                  <div className="boc__ticker-name"><TickerLabel symbol={ticker} /></div>
                  <div className="boc__ticker-sub">Configure time trigger + legs</div>
                </div>
                <button type="button" className="boc__ghost-btn" onClick={() => removeTicker(ticker)}>
                  Remove
                </button>
              </div>

              {/* ── time trigger ── */}
              <div className="boc__section">
                <div className="boc__section-title">Time Trigger</div>
                <div className="boc__grid boc__grid--3">
                  <div className={`boc__field ${hasError(ticker, 'execute_at_time') ? 'boc__field--error' : ''}`}>
                    <label className="label" htmlFor={`boc-${uid}-${tSafe}-exectime`}>
                      Execute at time <span className="boc__required">*</span>
                    </label>
                    <div className="boc__field-hint">Order fires when clock hits this time (CST).</div>
                    <input
                      id={`boc-${uid}-${tSafe}-exectime`}
                      className="input"
                      type="time"
                      step="1"
                      value={d.execute_at_time || ''}
                      onChange={(e) => handleFieldChange(ticker, 'execute_at_time', e.target.value)}
                    />
                  </div>

                  <div className="boc__field">
                    <label className="label" htmlFor={`boc-${uid}-${tSafe}-expirytime`}>
                      Order expiry time (optional)
                    </label>
                    <div className="boc__field-hint">Expire if not yet executed by this time.</div>
                    <input
                      id={`boc-${uid}-${tSafe}-expirytime`}
                      className="input"
                      type="time"
                      step="1"
                      value={d.order_expiry_time || ''}
                      onChange={(e) => handleFieldChange(ticker, 'order_expiry_time', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* ── legs ── */}
              <div className="boc__section">
                <div className="boc__section-title">Legs</div>
                <div className="boc__leg-grid">
                  {LEGS.map((leg) => (
                    <div className="boc__leg-card" key={leg}>
                      <div className="boc__leg-card-head">
                        <div className="boc__leg-title">{leg.toUpperCase()}</div>
                      </div>
                      <div className="boc__grid boc__grid--2">

                        {/* Strike mode */}
                        <div className="boc__field">
                          <label className="label" htmlFor={`boc-${uid}-${tSafe}-${leg}-smode`}>
                            Strike mode
                          </label>
                          <select
                            id={`boc-${uid}-${tSafe}-${leg}-smode`}
                            className="select"
                            value={d[`${leg}_strike_mode`] || 'pct'}
                            onChange={(e) => handleFieldChange(ticker, `${leg}_strike_mode`, e.target.value)}
                          >
                            <option value="pct">Strike %</option>
                            <option value="exact">Exact Strike</option>
                            <option value="dollar">$ Deviation</option>
                          </select>
                        </div>

                        {/* Strike value (adapts based on mode) */}
                        {(d[`${leg}_strike_mode`] || 'pct') === 'exact' ? (
                          <div className={`boc__field ${hasError(ticker, `${leg}_exact_strike`) ? 'boc__field--error' : ''}`}>
                            <label className="label" htmlFor={`boc-${uid}-${tSafe}-${leg}-exact`}>
                              Exact strike <span className="boc__required">*</span>
                            </label>
                            <div className="boc__field-hint">Strike price (e.g., 185.00)</div>
                            <input
                              id={`boc-${uid}-${tSafe}-${leg}-exact`}
                              className="input"
                              type="number"
                              step="0.01"
                              value={d[`${leg}_exact_strike`] || ''}
                              onChange={(e) => handleFieldChange(ticker, `${leg}_exact_strike`, e.target.value)}
                            />
                          </div>
                        ) : (d[`${leg}_strike_mode`] || 'pct') === 'dollar' ? (
                          <div className={`boc__field ${hasError(ticker, `${leg}_dollar_dev`) ? 'boc__field--error' : ''}`}>
                            <label className="label" htmlFor={`boc-${uid}-${tSafe}-${leg}-dollardev`}>
                              $ deviation <span className="boc__required">*</span>
                            </label>
                            <div className="boc__field-hint">Dollar deviation from current price</div>
                            <input
                              id={`boc-${uid}-${tSafe}-${leg}-dollardev`}
                              className="input"
                              type="number"
                              step="0.01"
                              min="0"
                              value={d[`${leg}_dollar_dev`] || ''}
                              onChange={(e) => handleFieldChange(ticker, `${leg}_dollar_dev`, e.target.value)}
                            />
                          </div>
                        ) : (
                          <div className={`boc__field ${hasError(ticker, `${leg}_strike_pct`) ? 'boc__field--error' : ''}`}>
                            <label className="label" htmlFor={`boc-${uid}-${tSafe}-${leg}-spct`}>
                              Strike % from price <span className="boc__required">*</span>
                            </label>
                            <div className="boc__field-hint">e.g., 1.5 means 1.5% away</div>
                            <input
                              id={`boc-${uid}-${tSafe}-${leg}-spct`}
                              className="input"
                              type="number"
                              step="0.01"
                              value={d[`${leg}_strike_pct`] || ''}
                              onChange={(e) => handleFieldChange(ticker, `${leg}_strike_pct`, e.target.value)}
                            />
                          </div>
                        )}

                        <div className="boc__field">
                          <label className="label" htmlFor={`boc-${uid}-${tSafe}-${leg}-pmode`}>
                            Premium mode
                          </label>
                          <select
                            id={`boc-${uid}-${tSafe}-${leg}-pmode`}
                            className="select"
                            value={d[`${leg}_premium_mode`] || 'pct'}
                            onChange={(e) => handleFieldChange(ticker, `${leg}_premium_mode`, e.target.value)}
                          >
                            <option value="pct">% of price</option>
                            <option value="dollar">$ limit</option>
                          </select>
                        </div>

                        <div className={`boc__field ${hasError(ticker, `${leg}_premium_pct`) ? 'boc__field--error' : ''}`}>
                          <label className="label" htmlFor={`boc-${uid}-${tSafe}-${leg}-ppct`}>
                            {d[`${leg}_premium_mode`] === 'dollar' ? 'Premium $ cap' : 'Premium %'} <span className="boc__required">*</span>
                          </label>
                          <input
                            id={`boc-${uid}-${tSafe}-${leg}-ppct`}
                            className="input"
                            type="number"
                            step="0.01"
                            value={d[`${leg}_premium_pct`] || ''}
                            onChange={(e) => handleFieldChange(ticker, `${leg}_premium_pct`, e.target.value)}
                            placeholder={d[`${leg}_premium_mode`] === 'dollar' ? 'e.g. 1.20' : 'e.g. 0.5'}
                          />
                        </div>

                        <div className={`boc__field ${hasError(ticker, `${leg}_direction`) ? 'boc__field--error' : ''}`}>
                          <label className="label" htmlFor={`boc-${uid}-${tSafe}-${leg}-dir`}>
                            Direction <span className="boc__required">*</span>
                            {leg !== 'core' && d.core_direction && <span className="boc__auto-tag"> (auto)</span>}
                          </label>
                          <select
                            id={`boc-${uid}-${tSafe}-${leg}-dir`}
                            className="select"
                            value={d[`${leg}_direction`] || ''}
                            onChange={(e) => handleFieldChange(ticker, `${leg}_direction`, e.target.value)}
                            disabled={leg !== 'core' && !!d.core_direction}
                          >
                            <option value="">Select&hellip;</option>
                            <option value="C">C (Call)</option>
                            <option value="P">P (Put)</option>
                          </select>
                        </div>

                        <div className={`boc__field ${hasError(ticker, `max_amount_${leg}`) ? 'boc__field--error' : ''}`}>
                          <label className="label" htmlFor={`boc-${uid}-${tSafe}-${leg}-amt`}>
                            Max amount ($) <span className="boc__required">*</span>
                          </label>
                          <input
                            id={`boc-${uid}-${tSafe}-${leg}-amt`}
                            className="input"
                            type="number"
                            step="1"
                            value={d[`max_amount_${leg}`] || ''}
                            onChange={(e) => handleFieldChange(ticker, `max_amount_${leg}`, e.target.value)}
                          />
                        </div>

                        <div className={`boc__field ${hasError(ticker, `${leg}_expiry_date`) ? 'boc__field--error' : ''}`}>
                          <label className="label" htmlFor={`boc-${uid}-${tSafe}-${leg}-exp`}>
                            Expiry date <span className="boc__required">*</span>
                          </label>
                          <input
                            id={`boc-${uid}-${tSafe}-${leg}-exp`}
                            className="input"
                            type="date"
                            value={d[`${leg}_expiry_date`] || ''}
                            onChange={(e) => handleFieldChange(ticker, `${leg}_expiry_date`, e.target.value)}
                          />
                        </div>

                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          );
        })}

        <div className="action-bar">
          <div className={`action-bar__status ${status.state === 'success' ? 'action-bar__status--success' : status.state === 'error' ? 'action-bar__status--error' : ''}`}>
            {status.state === 'idle' ? 'Ready' : status.message}
          </div>
          <button
            className="btn btn--primary"
            type="submit"
            disabled={status.state === 'loading' || tickers.length === 0}
          >
            {status.state === 'loading' ? 'Submitting\u2026' : 'Submit Strategy'}
          </button>
        </div>
      </form>

      {/* Order confirmation modal */}
      {showConfirmModal && (
        <OrderSummaryModal
          formData={formData}
          onConfirm={handleConfirmedSubmit}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}

      {/* In-app navigation guard */}
      {blocker.state === 'blocked' && (
        <div
          className="pbModal__backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="boc-nav-title"
        >
          <div className="pbModal pbModal--sm">
            <div className="pbModal__header">
              <h2 className="pbModal__title" id="boc-nav-title">
                Leave page?
              </h2>
              <p className="pbModal__subtitle">
                You have unsaved order data. Leaving now will discard everything
                you&apos;ve entered.
              </p>
            </div>
            <div className="pbModal__footer">
              <button
                type="button"
                className="pbGhostBtn"
                onClick={() => blocker.reset()}
              >
                Stay on page
              </button>
              <button
                type="button"
                className="pbDangerBtn"
                onClick={() => blocker.proceed()}
              >
                Leave anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
